import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { BoxImportant } from "@airhelp/icons";
import { Button, Flex, Radio, RadioGroup, Stack, Text, VStack, useToast, useBreakpointValue } from "@chakra-ui/react";

import ContentBox from "components/Flights/New/ContentBox";
import { EditedJourneyContext } from "contexts";
import { getMyProfile } from "hooks/api/userProfiles/useFetchMyProfile/useFetchMyProfile";
import { AirLuggageValueType } from "pages/Flights/AirLuggagePage";
import { EditableTypes } from "reducers/editableJourney";
import { ERROR_CODES } from "types/api";
import { useTrackEvent } from "utils/tracking/hooks";

interface IComponent {
  onNextStep: () => void;
  onPreviousStep: () => void;
  isSavingJourney: boolean;
  apiErrorCode: ERROR_CODES | null;
  setHasAirLuggageValue: (hasAirLuggageValue: AirLuggageValueType) => void;
  hasAirLuggageValue: string;
}

const AirLuggageView: React.FC<IComponent> = ({
  onNextStep,
  onPreviousStep,
  isSavingJourney,
  setHasAirLuggageValue,
  hasAirLuggageValue,
  apiErrorCode,
}) => {
  const { t } = useTranslation();
  const { trackPageInteractions } = useTrackEvent();
  const { dispatch } = useContext(EditedJourneyContext);
  const isMobile = useBreakpointValue({ base: true, md: false });
  const toast = useToast();
  const isDisabled = isSavingJourney || !!apiErrorCode;

  const userProfile = getMyProfile();

  const isMembershipComplete = userProfile?.policy.membership?.isComplete;
  const isMembershipEssential = userProfile?.policy.membership?.isEssential;

  const handleCheckboxChange = (newValue: AirLuggageValueType) => {
    setHasAirLuggageValue(newValue);
  };

  const handleContinueButton = () => {
    return onNextStep();
  };

  useEffect(() => {
    const applyAirLuggage = hasAirLuggageValue === "yes" ? true : false;
    dispatch({
      type: EditableTypes.SetApplyAirLuggage,
      payload: { applyAirLuggage },
    });
  }, [hasAirLuggageValue]);

  useEffect(() => {
    if (apiErrorCode) {
      const addJourneyBlocker = apiErrorCode === ERROR_CODES.ADD_JOURNEY_BLOCKER;

      toast({
        title: t(addJourneyBlocker ? "errors.sorry_you_cant_add_trip" : "errors.something_went_wrong"),
        description: addJourneyBlocker ? t("errors.please_contact_our_team") : null,
        status: "error",
        position: isMobile ? "bottom" : "top",
        isClosable: true,
        icon: <BoxImportant boxSize={5} alignSelf="center" color="greyscale.100" />,
        containerStyle: {
          fontSize: "sm",
          fontWeight: "500",
        },
      });

      if (addJourneyBlocker && (isMembershipComplete || isMembershipEssential)) {
        const trackMessage = isMembershipComplete
          ? "add trip error message complete"
          : "add trip error message essential";

        trackPageInteractions("show", trackMessage);
      }
    }
  }, [apiErrorCode]);

  return (
    <>
      <VStack spacing={6}>
        <ContentBox header={t("air_luggage.adding_luggage.header")}>
          <Flex flexDirection="column" maxWidth={{ base: "100%", lg: "410px" }}>
            <Text fontSize="md" mb={6} data-testid="adding-luggage-description">
              {t("air_luggage.adding_luggage.description")}
            </Text>
            <RadioGroup onChange={handleCheckboxChange} value={hasAirLuggageValue}>
              <Stack direction="column" spacing={0}>
                <Radio w="100%" value={"yes"} name="air-luggage-yes" variant="top" data-testid="air-luggage-yes">
                  {t("common.yes")}
                </Radio>
                <Radio w="100%" value={"no"} name="air-luggage-no" variant="bottom" data-testid="air-luggage-no">
                  {t("common.no")}
                </Radio>
              </Stack>
            </RadioGroup>
          </Flex>
        </ContentBox>
      </VStack>
      <Stack direction={{ base: "column", md: "row-reverse" }} mt={6} justifyContent="end" spacing={5}>
        <Button
          type="submit"
          minWidth={{ base: "100%", md: "80px" }}
          size="s"
          onClick={handleContinueButton}
          isLoading={isSavingJourney}
          isDisabled={isDisabled}
          data-testid="form-action-continue"
        >
          {t("funnels.add_new_flight.submit_trip")}
        </Button>
        <Button
          variant="secondary"
          minWidth={{ base: "100%", md: "80px" }}
          size="s"
          onClick={onPreviousStep}
          data-testid="form-action-back"
        >
          {t("funnels.back")}
        </Button>
      </Stack>
    </>
  );
};

export default AirLuggageView;
